import React from "react";
import { Accordion as BaseAccordion } from "@base-ui/react/accordion";
import { tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";

const useStyles = createStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[1],
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    textAlign: "left" as const,
    color: tokens.color.semantic.text.bold,
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    "font-weight": tokens.font.weight.semibold,
    lineHeight: tokens.font.lineHeight.normal,
  },
  chevron: {
    flexShrink: 0,
    transition: "transform 150ms",
    transform: "rotate(-90deg)",
    'button[aria-expanded="true"] &': {
      transform: "rotate(0deg)",
    },
  },
  panel: {
    border: `1px solid ${tokens.color.semantic.border.subtle}`,
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.color.semantic.bg.surface,
    padding: tokens.spacing[3],
    maxHeight: 320,
    overflowY: "auto" as const,
  },
}));

export interface AccordionItem {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

interface AccordionProps {
  items: AccordionItem[];
}

const slug = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const Accordion: React.FC<AccordionProps> = ({ items }) => {
  const { classes } = useStyles();
  const defaultValue = items.filter((item) => item.defaultOpen).map((item) => item.title);

  return (
    <BaseAccordion.Root multiple defaultValue={defaultValue} className={classes.root}>
      {items.map((item) => (
        <BaseAccordion.Item key={item.title} value={item.title}>
          <BaseAccordion.Header>
            <BaseAccordion.Trigger className={classes.trigger}>
              <IconV3
                icon="keyboard_arrow_down"
                size={24}
                color={tokens.color.semantic.icon.subtle}
                className={classes.chevron}
              />
              {item.title}
            </BaseAccordion.Trigger>
          </BaseAccordion.Header>
          <BaseAccordion.Panel className={classes.panel} data-testid={`accordion-panel-${slug(item.title)}`}>
            {item.children}
          </BaseAccordion.Panel>
        </BaseAccordion.Item>
      ))}
    </BaseAccordion.Root>
  );
};
